import type { ProjectGraphNode } from '@avisoma/shared';
import { CopyButton } from '../../components/CopyButton';
import { nodeLabel } from './graphModel';
import { IpcAncestry } from './IpcAncestry';
import { partDetailLines } from './partLife';
import { TaskSchedule } from './TaskSchedule';

type IdentifierProps = {
  label: string;
  value: string;
  /** What the button copies, when it is not `value` (a full id shown short). */
  copyText?: string;
  /** The button's name: what it copies (`Copy ESN`). */
  tooltip: string;
};

/** An identifier with its own copy button. */
export function Identifier({ label, value, copyText = value, tooltip }: IdentifierProps) {
  return (
    <span className="inline-flex items-center gap-1">
      <span>
        {label} {value}
      </span>
      <CopyButton text={copyText} tooltip={tooltip} size={12} className="text-zinc-400 dark:text-zinc-500" />
    </span>
  );
}

/** The selected entity's identifiers, each with a copy button, and its other details. */
export function EntityHeading({ node }: { node: ProjectGraphNode }) {
  switch (node.kind) {
    case 'engine':
      return (
        <div className="font-medium">
          <Identifier label="ESN" value={node.esn} tooltip="Copy ESN" />
          {node.engineType ? ` · ${node.engineType}` : ''}
          {node.isWorkingEngine ? ' · working engine' : node.role === 'donor' ? ' · donor engine' : ''}
        </div>
      );
    case 'aircraft':
      return (
        <div className="font-medium">
          <Identifier label="Aircraft" value={node.registration} tooltip="Copy registration" />
          {node.msn ? ` · MSN ${node.msn}` : ''}
          {node.aircraftType ? ` · ${node.aircraftType}` : ''}
        </div>
      );
    case 'part': {
      const details = partDetailLines(node);
      return (
        <>
          <div className="font-medium">
            <Identifier label="P/N" value={node.partNumber} tooltip="Copy P/N" /> ·{' '}
            <Identifier label="S/N" value={node.serialNumber} tooltip="Copy S/N" />
            {node.description ? ` · ${node.description}` : ''}
          </div>
          {details.length > 0 && (
            <div className="text-zinc-500 dark:text-zinc-400">
              {details.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          )}
        </>
      );
    }
    case 'reference': {
      const refKind = node.refKind.toUpperCase();
      return (
        <>
          <div className="font-medium">
            <Identifier label={refKind} value={node.number} tooltip={`Copy ${refKind} reference`} />
            {node.authority ? ` (${node.authority})` : ''}
            {node.title ? ` · ${node.title}` : ''}
          </div>
          <TaskSchedule schedule={node.schedule} unknown={node.unknown} directive={node.directive} />
        </>
      );
    }
    case 'ipc':
      return (
        <>
          <div className="font-medium break-all">{nodeLabel(node)}</div>
          <IpcAncestry ancestors={node.ancestors} />
        </>
      );
    default:
      return <div className="font-medium break-all">{nodeLabel(node)}</div>;
  }
}
